import { z } from "zod";
import type { Color } from "../color";
import { colorToHex } from "../color-conversions/color-to-hex";
import type { ColorSet } from "../color-sets/color-set";
import {
    type ColorSetColor,
    colorSetColors,
} from "../color-sets/color-set-names";
import {
    type ColorThemeSetName,
    colorThemeSetNames,
} from "../color-sets/color-theme-sets";
import type { ColorTheme } from "../color-theme/color-theme";
import { type HexColorSet, hexColorSetZodSchema } from "./hex-color-set";

type HexColorTheme = {
    _type: "HEX_COLOR_THEME";
    default?: HexColorSet;
    primary?: HexColorSet;
    secondary?: HexColorSet;
    tertiary?: HexColorSet;
    quaternary?: HexColorSet;
    warning?: HexColorSet;
    information?: HexColorSet;
    danger?: HexColorSet;
    textInput?: HexColorSet;
    disabled?: HexColorSet;
};

const hexColorSetNames = [
    "default",
    "primary",
    "secondary",
    "tertiary",
    "quaternary",
    "warning",
    "information",
    "danger",
    // "textInput",
    "disabled",
] as const;
const hexColorSetNameZodEnum = z.enum(hexColorSetNames);
type HexColorSetName = (typeof hexColorSetNames)[number];

const hexColorThemeZodSchema: z.ZodSchema<HexColorTheme> = z.object({
    _type: z.literal("HEX_COLOR_THEME"),
    default: hexColorSetZodSchema,
    primary: hexColorSetZodSchema,
    secondary: hexColorSetZodSchema,
    tertiary: hexColorSetZodSchema,
    quaternary: hexColorSetZodSchema,
    warning: hexColorSetZodSchema,
    information: hexColorSetZodSchema,
    danger: hexColorSetZodSchema,
    textInput: hexColorSetZodSchema,
    disabled: hexColorSetZodSchema,
});

function hexColorThemeFactory(params: ColorTheme) {
    // console.log("HEX COLOR THEME FACTORY", params);
    const hexColorTheme = Object.entries(params).reduce<HexColorTheme>(
        (output, [colorThemeKey, colorSet]) => {
            // Only keep color theme set props, ignore the rest
            if (!colorThemeSetNames.some(name => name === colorThemeKey)) {
                return output;
            }
            const _colorThemeSetName = colorThemeKey as ColorThemeSetName;
            const _colorSet = colorSet as ColorSet;
            for (const [key, value] of Object.entries(_colorSet)) {
                if (!colorSetColors.some(name => name === key)) {
                    continue;
                }
                const _key = key as ColorSetColor;
                const _color = value as Color;
                const _hexColor = colorToHex(_color);
                output[_colorThemeSetName] = {
                    ...(output[_colorThemeSetName] || {}),
                    _type: "HEX_COLOR_SET",
                    [_key]: _hexColor,
                };
            }
            return output;
        },
        {
            _type: "HEX_COLOR_THEME",
        } as HexColorTheme,
    );

    // console.log("HEX COLOR THEME OUTPUT", hexColorTheme);
    return hexColorTheme;
}

export {
    hexColorSetNames,
    hexColorSetNameZodEnum,
    hexColorThemeFactory,
    hexColorThemeZodSchema,
};
export type { HexColorSet, HexColorSetName, HexColorTheme };
